import { useEffect, useRef, useState } from "react";
import api, { apiError, imgSrc } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ProductThumb } from "@/pages/QuoteBuilder";
import { formatINR } from "@/lib/format";
import { Paperclip, Upload, Package, Loader2, FileText, Search, X } from "lucide-react";

const ACCEPT = "image/jpeg,image/png,image/webp,video/mp4,video/3gpp,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,text/plain";

// Paperclip menu for the WhatsApp composer: upload a device file or share a catalogue product photo.
export function AttachMenu({ conv, disabled, onSent, size = "h-10 w-10", testid = "attach" }) {
  const fileRef = useRef();
  const [file, setFile] = useState(null);
  const [catalog, setCatalog] = useState(false);
  return (
    <>
      <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" data-testid={`${testid}-file-input`}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) setFile(f); e.target.value = ""; }} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" disabled={disabled} className={`${size} p-0 shrink-0`} title="Attach" data-testid={`${testid}-btn`}><Paperclip className="w-4 h-4 text-[#101D51]" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onClick={() => fileRef.current?.click()} data-testid={`${testid}-upload`}><Upload className="w-4 h-4 mr-2" /> Photo, video or file</DropdownMenuItem>
          <DropdownMenuItem onClick={() => setCatalog(true)} data-testid={`${testid}-catalog`}><Package className="w-4 h-4 mr-2" /> From catalogue</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {file && <UploadDialog conv={conv} file={file} onClose={() => setFile(null)} onSent={() => { setFile(null); onSent(); }} />}
      {catalog && <CatalogPicker conv={conv} onClose={() => setCatalog(false)} onSent={() => { setCatalog(false); onSent(); }} />}
    </>
  );
}

function UploadDialog({ conv, file, onClose, onSent }) {
  const [caption, setCaption] = useState("");
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const kind = file.type.startsWith("image/") ? "image" : file.type.startsWith("video/") ? "video" : file.type.startsWith("audio/") ? "audio" : "document";
  useEffect(() => {
    if (kind === "image" || kind === "video") { const u = URL.createObjectURL(file); setPreview(u); return () => URL.revokeObjectURL(u); }
    return undefined;
  }, [file, kind]);
  const mb = (file.size / 1048576).toFixed(1);
  const send = async () => {
    setSending(true);
    const fd = new FormData(); fd.append("file", file); if (caption.trim()) fd.append("caption", caption.trim());
    try { await api.post(`/conversations/${conv.id}/attachments`, fd); toast.success("Attachment sent"); onSent(); }
    catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="attach-upload-dialog">
        <DialogHeader><DialogTitle>Send {kind}</DialogTitle></DialogHeader>
        <div className="rounded-xl bg-[#F5F6FA] border border-[#E6E8F0] p-2 flex items-center justify-center min-h-[140px]">
          {kind === "image" && preview && <img src={preview} alt="" className="max-h-64 rounded-lg object-contain" data-testid="attach-preview-image" />}
          {kind === "video" && preview && <video src={preview} controls className="max-h-64 rounded-lg" />}
          {(kind === "document" || kind === "audio") && <div className="flex items-center gap-3 text-sm text-[#101D51]"><FileText className="w-8 h-8 text-[#E02424]" /><div><div className="font-medium break-all">{file.name}</div><div className="text-xs text-[#6B7189]">{mb} MB</div></div></div>}
        </div>
        <div className="text-xs text-[#6B7189]">{file.name} · {mb} MB</div>
        {kind !== "audio" && <Textarea rows={2} placeholder="Add a caption (optional)" value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="attach-caption" />}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#0B7C5A] hover:bg-[#09664a]" onClick={send} disabled={sending} data-testid="attach-send">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send on WhatsApp"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>);
}

function CatalogPicker({ conv, onClose, onSent }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [pick, setPick] = useState(null);
  const [caption, setCaption] = useState("");
  const [sending, setSending] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => api.get(`/products?q=${encodeURIComponent(q)}&page_size=40`).then((r) => setItems(r.data.items)), 200);
    return () => clearTimeout(t);
  }, [q]);
  const send = async () => {
    setSending(true);
    try { await api.post(`/conversations/${conv.id}/share-product`, { product_id: pick.id, caption: caption.trim() || null }); toast.success(`${pick.name} shared`); onSent(); }
    catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="attach-catalog-dialog">
        <DialogHeader><DialogTitle>Share a product</DialogTitle></DialogHeader>
        {!pick ? (
          <>
            <div className="relative"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7189]" />
              <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className="pl-9 h-10" data-testid="attach-catalog-search" /></div>
            <div className="max-h-80 overflow-y-auto divide-y divide-[#F0F1F5]">
              {items.map((p) => (
                <button key={p.id} onClick={() => setPick(p)} className="w-full flex items-center gap-3 py-2 px-1 hover:bg-[#F5F6FA] rounded text-left" data-testid={`attach-product-${p.code}`}>
                  <ProductThumb url={p.image_url} size={10} />
                  <div className="min-w-0 flex-1"><div className="text-sm font-medium text-[#101D51] truncate">{p.name}</div><div className="text-[11px] text-[#6B7189]">{p.code} · {p.brand}{!p.image_url && " · no photo, text only"}</div></div>
                  <div className="text-sm font-semibold text-[#101D51]">{formatINR(p.list_price)}</div>
                </button>))}
              {items.length === 0 && <div className="text-sm text-[#6B7189] text-center py-6">No products found</div>}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-start gap-3 rounded-xl bg-[#F5F6FA] border border-[#E6E8F0] p-3">
              {pick.image_url ? <img src={imgSrc(pick.image_url)} alt="" className="w-28 h-28 rounded-lg object-contain bg-white border border-[#E6E8F0]" /> : <ProductThumb url={null} size={28} />}
              <div className="min-w-0 flex-1 text-sm">
                <div className="font-semibold text-[#101D51]">{pick.name}</div>
                <div className="text-xs text-[#6B7189]">{pick.code}{pick.brand ? ` · ${pick.brand}` : ""}</div>
                <div className="mt-1 font-semibold text-[#0B7C5A]">{formatINR(pick.list_price)} <span className="text-xs font-normal text-[#6B7189]">per {pick.unit} · +{Math.round(pick.gst_rate * 100)}% GST</span></div>
                <button onClick={() => setPick(null)} className="text-xs text-[#6B7189] underline mt-2 inline-flex items-center gap-1"><X className="w-3 h-3" /> choose another</button>
              </div>
            </div>
            <Textarea rows={3} value={caption} onChange={(e) => setCaption(e.target.value)} placeholder={`Caption (default: name, code and price)`} data-testid="attach-catalog-caption" />
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button className="bg-[#0B7C5A] hover:bg-[#09664a]" onClick={send} disabled={sending} data-testid="attach-catalog-send">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send on WhatsApp"}</Button>
            </DialogFooter>
          </>)}
      </DialogContent>
    </Dialog>);
}
